import React, { ReactNode, useContext, forwardRef, ForwardedRef } from 'react';
import clsx from 'clsx';
import { ThemeContext, ThemeContextProps } from '@danske/sapphire-react-context';
import { Theme } from '@danske/sapphire-css/themes';
import { SapphireStyleProps, useSapphireStyleProps } from '../../utils';
import { ThemeVariant } from '../../SapphireProvider/src';
import surfaceStyles from '@danske/sapphire-css/components/surface/surface.module.css';
import { SapphireViewStyleProps } from '../../View';
import { DOMProps, DOMAttributes } from '@react-types/shared';
import { viewStylePropHandlers } from '../../View/src/View';
import { filterDOMProps } from 'react-aria/filterDOMProps';

export interface ThemeRootProps
  extends SapphireStyleProps,
    Omit<SapphireViewStyleProps, 'backgroundColor' | 'color'>,
    DOMProps,
    Omit<
      DOMAttributes,
      'children' | 'style' | 'className' | 'dangerouslySetInnerHTML'
    > {
  /**
   * The theme variation, if the theme supports any.
   */
  variant?: ThemeVariant;
  noSurface?: boolean;
  children: ReactNode;
}

/**
 * ThemeRoot wraps the children with an element that provides the design tokens
 * in the DOM subtree. Also renders the necessary background surface for the
 * theme's components to be rendered on.
 */
export const ThemeRoot = forwardRef(function ThemeRoot(
  { children, variant, noSurface = false, ...props }: ThemeRootProps,
  ref: ForwardedRef<HTMLDivElement>
) {
  const themeContext = useContext(ThemeContext);

  if (typeof themeContext.theme === 'undefined') {
    throw new Error(
      '<ThemeRoot> must be rendered inside a <SapphireProvider>.'
    );
  }
  const { styleProps } = useSapphireStyleProps(props, viewStylePropHandlers);
  const theme = themeContext.theme as ThemeContextProps<
    Theme,
    ThemeVariant
  >['theme'];
  const themeVariant =
    variant ||
    (themeContext.themeVariant as ThemeContextProps<
      Theme,
      ThemeVariant
    >['themeVariant']);

  const themeClassName = theme?.themeClassName;
  const colorScheme = themeClassName?.includes('dark') ? 'dark' : 'light';

  return (
    <div
      className={clsx(
        themeClassName,
        themeVariant === 'secondary'
          ? theme?.themeSecondaryModifierClassName
          : themeVariant === 'tertiary'
          ? theme?.themeTertiaryModifierClassName
          : themeVariant === 'contrast'
          ? theme?.themeContrastModifierClassName
          : null,
        styleProps.className,
        !noSurface && surfaceStyles['sapphire-surface']
      )}
      style={{ ...styleProps.style, colorScheme }}
      ref={ref}
      {...filterDOMProps(props, { global: true })}
    >
      {children}
    </div>
  );
});
